import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

const MIN_PERCENT = 30;
const MAX_PERCENT = 78;

/**
 * Two columns with a draggable divider. The chosen width survives reloads —
 * how someone splits their screen is a working preference, not a per-visit
 * decision.
 */
export function SplitPane({
  storageKey,
  left,
  right,
  defaultPercent = 50,
}: {
  storageKey: string;
  left: ReactNode;
  right: ReactNode;
  defaultPercent?: number;
}) {
  const [percent, setPercent] = useState(() => {
    const stored = Number(localStorage.getItem(storageKey));
    return stored >= MIN_PERCENT && stored <= MAX_PERCENT ? stored : defaultPercent;
  });
  const [active, setActive] = useState("inventory");
  const [dragging, setDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    localStorage.setItem(storageKey, String(Math.round(percent)));
  }, [percent, storageKey]);

  const moveTo = useCallback((clientX: number) => {
    const box = containerRef.current?.getBoundingClientRect();
    if (!box) return;
    const next = ((clientX - box.left) / box.width) * 100;
    setPercent(Math.min(MAX_PERCENT, Math.max(MIN_PERCENT, next)));
  }, []);

  useEffect(() => {
    if (!dragging) return;
    const onMove = (event: PointerEvent) => moveTo(event.clientX);
    const stop = () => setDragging(false);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", stop);
    // The iframe/canvas case: a drag that leaves the window must still end.
    window.addEventListener("blur", stop);
    window.addEventListener("pointercancel", stop);
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", stop);
      window.removeEventListener("blur", stop);
      window.removeEventListener("pointercancel", stop);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };
  }, [dragging, moveTo]);

  return (
    <div className="split-pane" ref={containerRef} data-active={active}>
      <div className="compact-pane-switch" role="group" aria-label="Arbeitsbereich">
        <button type="button" className="tab" aria-pressed={active === "inventory"} onClick={() => setActive("inventory")}>Bestand</button>
        <button type="button" className="tab" aria-pressed={active === "assistant"} onClick={() => setActive("assistant")}>Assistent</button>
      </div>
      <div className="split-pane-left" style={{ flexBasis: `${percent}%` }}>
        {left}
      </div>
      <div
        className={"split-pane-divider" + (dragging ? " dragging" : "")}
        role="separator"
        aria-orientation="vertical"
        aria-valuenow={Math.round(percent)}
        aria-valuemin={MIN_PERCENT}
        aria-valuemax={MAX_PERCENT}
        aria-label="Breite von Liste und Chat"
        tabIndex={0}
        onPointerDown={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDoubleClick={() => setPercent(defaultPercent)}
        onKeyDown={(event) => {
          if (event.key === "ArrowLeft") setPercent((p) => Math.max(MIN_PERCENT, p - 2));
          if (event.key === "ArrowRight") setPercent((p) => Math.min(MAX_PERCENT, p + 2));
        }}
      >
        <span className="split-pane-grip" />
      </div>
      <div className="split-pane-right">{right}</div>
    </div>
  );
}
